import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Box, Container, Grid } from "@mui/material";
import {
  Alert,
  Button,
  SectionNavigation,
  SectionNavigationItem,
  Typography,
} from "@platform/design-system";
import { useRouteMatch } from "@platform/hooks";
import { useSignedInUser } from "@platform/user/client";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import { submitEvent } from "../../utils/tracking";
import { Loader } from "../../components";
import ColoredBanner from "../../components/coloredBanner";
import { ROUTE_NAMES } from "../../constants";
import { CLIENT_TYPES } from "../../constants/client";
import { useClient } from "../../hooks/useClient";
import {
  CoveragesData,
  isInternalUser,
  normalizeClientProducts,
  extractErrorMessage,
} from "../../utils";

import { ClientDetailsContext, ClientRfpsContext } from "./types";

const Client = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [coverages, setCoverages] = useState<CoveragesData[]>([]);
  const route = useRouteMatch(ROUTE_PATHS, {
    baseRoute: `/${ROUTE_NAMES.CLIENTS}/:id/`,
  });
  const navigate = useNavigate();

  const {
    role,
    isAdmin,
    isAnalyst,
    isConsultant,
    isAdminOrConsultant: adminOrConsultant,
  } = useSignedInUser();
  const internalUser = isInternalUser(role);
  const eventSent = useRef(false);

  const query = useClient({ clientId: id ?? "" });

  const { data: client, isSuccess, isLoading, isError, error } = query;

  useEffect(() => {
    if (client?.products) {
      setCoverages(normalizeClientProducts(client.products));
    }
  }, [client?.products]);

  useEffect(() => {
    if (isSuccess && !eventSent.current && id) {
      submitEvent("Client Details Viewed", {
        clientId: parseInt(id),
        clientName: client.name,
        brokerName: client.brokerName,
      });
      eventSent.current = true;
    }
  }, [isSuccess, id, client]);

  if (isLoading) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <Box mt={15} mb={2}>
          <Alert severity="error">{extractErrorMessage(error)}</Alert>
        </Box>
      </Container>
    );
  }

  const context: ClientRfpsContext | ClientDetailsContext =
    route === ROUTE_NAMES.RFPS
      ? { data: client }
      : {
          client,
          isAdmin,
          isAnalyst,
          clientId: id,
          coverages,
          setCoverages,
          isConsultant,
        };

  return (
    <Box>
      <ColoredBanner>
        <Grid container direction="row">
          <Grid size={12} justifyContent="space-between" display="flex">
            <Button
              href={`/${ROUTE_NAMES.CLIENTS}`}
              startIcon={<ArrowBackIcon />}
              variant="tertiary"
            >
              Back
            </Button>
            {!internalUser && (
              <Button
                data-testid="start-rfp-button"
                variant="primary"
                sx={{ alignSelf: "end" }}
                onClick={() => {
                  submitEvent("Start RFP");
                  navigate(`/${ROUTE_NAMES.CLIENT_SELECTION}`, {
                    state: {
                      client: {
                        clientId: parseInt(id),
                        name: client.name,
                        clientType: CLIENT_TYPES.CURRENT_CLIENT,
                      },
                      prevPath: location.pathname,
                    },
                  });
                }}
              >
                Start RFP
              </Button>
            )}
          </Grid>
          <Grid size={12}>
            <Typography variant="display-medium" mt={3}>
              {client.name}
            </Typography>
            {adminOrConsultant && (
              <Typography variant="subtitle1" color="textSecondary">
                Registered Broker: <b>{client.brokerName}</b>
              </Typography>
            )}
          </Grid>
        </Grid>
      </ColoredBanner>
      <Container>
        <Grid container mb={5}>
          <Grid
            size={12}
            sx={{ borderBottom: 1, borderColor: "divider" }}
            mt={3}
          >
            <SectionNavigation navItems={NAV_ITEMS} value={route} />
          </Grid>
          <Box width="100%">
            <Outlet context={context} />
          </Box>
        </Grid>
      </Container>
    </Box>
  );
};

type RouteKey = "details" | "rfps";

const NAV_ITEMS: SectionNavigationItem<RouteKey>[] = [
  {
    label: "Client Details",
    path: "",
    value: "details",
    icon: <PersonOutlinedIcon />,
    iconPosition: "start",
  },
  {
    label: "RFPs",
    path: ROUTE_NAMES.RFPS,
    value: "rfps",
    icon: <InsertDriveFileOutlinedIcon />,
    iconPosition: "start",
  },
];

const ROUTE_PATHS = NAV_ITEMS.map<[RouteKey, string]>((item) => [
  item.value,
  item.path,
]);

export default Client;
